import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import SectionLabel from "./SectionLabel";
import { FadeUp, MaskLine } from "./Reveal";

const steps = ["THE TABLE", "THE GATHERING", "THE BRIEFING"];

export default function Bridge() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section ref={ref} data-testid="bridge-section" className="relative border-b border-ink/15">
      <div className="mx-auto max-w-[88rem] px-6 py-28 md:px-10 md:py-40">
        <SectionLabel testid="bridge-label">The Story Behind The Gathering</SectionLabel>

        <h2 className="max-w-3xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          <MaskLine>Before there was RECON,</MaskLine>
          <MaskLine delay={0.1}>
            there was <span className="italic text-wine">a table.</span>
          </MaskLine>
        </h2>

        <div className="mt-16 grid gap-14 lg:grid-cols-12">
          <FadeUp delay={0.15} className="max-w-xl space-y-6 text-base leading-relaxed text-smoke md:text-lg lg:col-span-6">
            <p>
              WMWE began with eight women gathered around a table. They didn't gather for
              performance. They didn't gather to impress one another. They gathered for
              conversation. For prayer. For wisdom. For truth. For vulnerability. For the kind of
              connection that reminds you that you don't have to carry everything alone.
            </p>
            <p>
              That table became the foundation of We Meet We Eat. And over the years, WMWE has
              continued creating spaces where women can bring both their brilliance and their
              burdens — where wisdom is exchanged and conversations shift perspective.
            </p>
            <p className="text-ink">
              RECON is an evolution of that table. Because sometimes, before the next conversation,
              the next decision, the next season, the next move —{" "}
              <span className="font-medium text-wine">you need to gather intelligence.</span>
            </p>
          </FadeUp>

          <div className="relative lg:col-span-6">
            <motion.figure
              style={{ y: imgY }}
              initial={{ opacity: 0, rotate: 2.5, scale: 0.97 }}
              whileInView={{ opacity: 1, rotate: 1.5, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative ml-auto max-w-md border border-ink/25 bg-bone p-3 shadow-[0_30px_60px_-30px_rgba(26,26,26,0.45)]"
            >
              <span
                aria-hidden="true"
                className="absolute -top-3 left-8 h-6 w-20 -rotate-3 border border-ink/10 bg-blush/60"
              />
              <img
                src="/assets/wmwe-toast-2021.jpg"
                alt="Women raising a toast around a candlelit WMWE table, 2021"
                loading="lazy"
                data-testid="bridge-table-image"
                className="aspect-[4/3] w-full object-cover"
              />
              <figcaption className="mt-3 flex justify-between font-sans text-[9px] tracking-[0.3em] text-ink/50">
                <span>ARCHIVE 001</span>
                <span>WMWE GATHERING — 2021</span>
              </figcaption>
            </motion.figure>
          </div>
        </div>

        <div className="mt-24 flex flex-col items-start gap-4 md:flex-row md:items-center md:gap-0">
          {steps.map((step, i) => (
            <FadeUp key={step} delay={i * 0.18} className="flex items-center">
              <span
                className={`border px-6 py-3 font-sans text-[11px] tracking-[0.35em] ${
                  i === steps.length - 1
                    ? "border-wine bg-wine text-bone"
                    : "border-ink/30 text-ink/80"
                }`}
              >
                {step}
              </span>
              {i < steps.length - 1 && (
                <span className="mx-4 hidden font-sans text-gold md:block">→</span>
              )}
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
